import { useState, useEffect } from 'react'
import { navAPI, siteAPI, uploadFile } from '../services/api'
import { useNotification } from '../context/NotificationContext'
import { getErrorMessage, getImageUrl, confirmAction } from '../utils/helpers'
import config from '../config'
import CustomSelect from '../components/common/CustomSelect'

const NavbarPages = () => {
  const { showSuccess, showError } = useNotification()
  const [nav, setNav] = useState([])
  const [settings, setSettings] = useState({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [navRes, siteRes] = await Promise.all([navAPI.getAll(), siteAPI.getSettings()])
      setNav(navRes.data.nav || [])
      setSettings(siteRes.data.settings || {})
    } catch (error) {
      showError(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  const handleNavSubmit = async (e, id = null) => {
    e.preventDefault()
    const formData = new FormData(e.target)
    const data = {
      label: formData.get('label'),
      url: formData.get('url'),
      page_key: formData.get('page_key') || null,
      icon: formData.get('icon') || null,
      sort_order: formData.get('sort_order'),
      visible: formData.get('visible') ? 1 : 0,
    }

    try {
      if (id) {
        await navAPI.update(id, data)
        showSuccess('Nav item updated.')
      } else {
        await navAPI.create(data)
        showSuccess('Nav item added.')
      }
      fetchData()
      if (!id) e.target.reset()
    } catch (error) {
      showError(getErrorMessage(error))
    }
  }

  const handleDeleteNav = async (id) => {
    if (!confirmAction('Delete this nav item?')) return
    try {
      await navAPI.delete(id)
      showSuccess('Nav item deleted.')
      fetchData()
    } catch (error) {
      showError(getErrorMessage(error))
    }
  }

  const handleHeroImageUpload = async (e, target) => {
    e.preventDefault()
    const formData = new FormData(e.target)
    const imageFile = formData.get('image')
    const remove = formData.get('remove') === '1'

    try {
      if (remove) {
        await siteAPI.updateSettings({ [target]: '' })
        showSuccess('Page hero image removed.')
      } else if (imageFile && imageFile.size > 0) {
        const uploadRes = await uploadFile(imageFile)
        const uploadedImg = uploadRes.data.filename || uploadRes.data.image
        await siteAPI.updateSettings({ [target]: uploadedImg })
        showSuccess('Page hero image updated.')
      } else {
        showError('Choose an image file to upload.')
        return
      }
      fetchData()
      e.target.reset()
    } catch (error) {
      showError(getErrorMessage(error))
    }
  }

  if (loading) return <div className="admin-page">Loading...</div>

  const pageOptions = [
    ['home', 'Home'],
    ['about', 'About Us'],
    ['services', 'Services'],
    ['gallery', 'Gallery'],
    ['blog', 'Blog'],
    ['reviews', 'Reviews'],
    ['contact', 'Contact Us'],
  ]

  const heroImages = [
    ['hero_img_about', 'About Us', '/about'],
    ['hero_img_gallery', 'Gallery', '/gallery'],
    ['hero_img_blog', 'Blog', '/blog'],
    ['hero_img_contact', 'Contact Us', '/contact'],
  ]

  return (
    <div className="admin-page">
      <h2 className="admin-title">Navbar &amp; Pages</h2>
      <p className="admin-sub">Manage the website's navigation menu and page hero images.</p>

      <div className="row">
        <div className="col-lg-8">
          <div className="panel">
            <div className="panel-head">Existing Navbar Items</div>
            {nav.length > 0 ? (
              nav.map((item) => (
                <div key={item.id} className="item-row">
                  <form onSubmit={(e) => handleNavSubmit(e, item.id)} className="item-edit">
                    <input type="number" name="sort_order" defaultValue={item.sort_order} className="form-control form-control-sm" style={{width: '64px'}} title="Order" />
                    <input type="text" name="label" defaultValue={item.label} className="form-control form-control-sm" required title="Label" />
                    <input type="text" name="url" defaultValue={item.url} className="form-control form-control-sm item-desc" required title="URL" />
                    <CustomSelect
                      name="page_key"
                      defaultValue={item.page_key || ''}
                      placeholder="— none —"
                      small
                      options={[
                        { value: '', label: '— none —' },
                        ...pageOptions.map(([key, label]) => ({ value: key, label }))
                      ]}
                    />
                    <input type="text" name="icon" defaultValue={item.icon || ''} className="form-control form-control-sm" style={{width: '120px'}} placeholder="Icon" />
                    <label className="checkbox-inline mb-0">
                      <input type="checkbox" name="visible" defaultChecked={item.visible} /> Visible
                    </label>
                    <button className="btn btn-sm btn-gold"><i className="fa fa-check"></i></button>
                  </form>
                  <button onClick={() => handleDeleteNav(item.id)} className="btn btn-sm btn-danger">
                    <i className="fa fa-trash"></i>
                  </button>
                </div>
              ))
            ) : (
              <p className="muted">No nav items yet.</p>
            )}
          </div>

          <div className="panel mt-3">
            <div className="panel-head">Add Navbar Item</div>
            <form onSubmit={handleNavSubmit}>
              <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Label *</label>
                    <input type="text" name="label" className="form-control" required />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>URL *</label>
                    <input type="text" name="url" className="form-control" required />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Page key</label>
                      <CustomSelect
                        name="page_key"
                        placeholder="— none —"
                        options={[
                          { value: '', label: '— none —' },
                          ...pageOptions.map(([key, label]) => ({ value: key, label }))
                        ]}
                      />
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Icon class</label>
                    <input type="text" name="icon" className="form-control" placeholder="fa-tag" />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Display order</label>
                    <input type="number" name="sort_order" className="form-control" defaultValue="10" />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>&nbsp;</label>
                    <label className="checkbox-inline mt-2">
                      <input type="checkbox" name="visible" defaultChecked /> Visible in navbar
                    </label>
                  </div>
                </div>
              </div>
              <button className="btn btn-gold btn-block">Add Navbar Item</button>
            </form>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="panel">
            <div className="panel-head">Page Hero Images</div>
            <p className="muted">Set background images for page hero banners.</p>

            {heroImages.map(([key, label, link]) => (
              <div key={key} style={{marginTop: '20px', borderTop: '1px solid var(--line)', paddingTop: '18px'}}>
                <label>
                  {label} 
                  <a href={config.web.baseUrl + link} target="_blank" rel="noopener noreferrer" className="muted" style={{fontSize: '12px', marginLeft: '8px'}}>
                    view →
                  </a>
                </label>
                {settings[key] && (
                  <div className="file-preview">
                    <span>Current hero image</span>
                    <img src={getImageUrl(settings[key])} alt={label} />
                  </div>
                )}
                <form onSubmit={(e) => handleHeroImageUpload(e, key)} style={{marginTop: '10px'}}>
                  <input type="file" name="image" className="form-control form-control-sm" accept="image/*" />
                  {settings[key] && (
                    <label className="checkbox-inline mt-2">
                      <input type="checkbox" name="remove" value="1" /> Remove current image
                    </label>
                  )}
                  <div style={{marginTop: '8px'}}>
                    <button className="btn btn-gold btn-sm">
                      <i className="fa fa-cloud-upload"></i> Upload Hero Image
                    </button>
                  </div>
                </form>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default NavbarPages
